import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import FestivalPassModal from './FestivalPassModal';
import './UpsellBanner.css';

/**
 * Post-purchase upsell banner.
 *
 * Lifecycle & guarantees:
 *   1. On mount, fetches the offer from GET /api/payments/upsell/<session_id>/
 *      which authoritatively starts the 20-min server-side timer.
 *   2. Runs a local per-second countdown from `remaining_seconds` (refresh
 *      cannot extend it — the backend re-emits the true value).
 *   3. Once the banner is shown, a `localStorage` flag is dropped so that
 *      the Cart hides its "Have a coupon?" input for the remainder of the
 *      window and, if the buyer clicks "Add Seats", auto-applies the
 *      UPSELL-AMP-20 coupon AND tags the next checkout with
 *      `upsell_source_session_id = <this sessionId>` so the follow-on
 *      success page does not show another upsell.
 *   4. Close-detection: a `sessionStorage` flag is set on first render;
 *      if the user closes the tab and returns, the flag is gone and we
 *      report `closed` to the backend instead of re-showing the banner.
 *      Terminal statuses (accepted/declined/closed/expired) are sticky on
 *      the backend — once any of them is set, GET always returns
 *      `available: false`.
 *   5. sendBeacon on pagehide → best-effort `closed` event.
 *   6. Every user interaction (view / click / decline / accept / close /
 *      expire) is reported to POST /track/ for analytics.
 */

// LocalStorage keys shared with Cart.jsx.
const LS_WINDOW_KEY = 'upsellWindow';        // { sessionId, caseKey, expiresAt }
const LS_COUPON_KEY = 'upsellAmpCoupon';     // { sessionId, code, expiresAt }
// SessionStorage keys (per-tab) for close detection.
const SS_SEEN_PREFIX = 'upsell_seen_';       // sessionStorage flag per sessionId
// LocalStorage bookkeeping for "ever seen from this browser" so we can tell
// close-and-reopen from first-load.
const LS_EVER_PREFIX = 'upsell_ever_';

const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

const safeLSGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeLSSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };
const safeLSDel = (k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } };
const safeSSGet = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const safeSSSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } };

/**
 * Decide whether the banner should even attempt to render.
 *   - Same tab (sessionStorage flag present)          → allow.
 *   - First-ever load (no localStorage mark)          → allow.
 *   - New tab after close (LS mark, no SS)            → deny (report 'closed').
 */
const resolveTabLifecycle = (sessionId) => {
  if (!sessionId) return { allow: false, reason: 'no_session' };
  const ssKey = SS_SEEN_PREFIX + sessionId;
  const lsKey = LS_EVER_PREFIX + sessionId;
  const ssFlag = safeSSGet(ssKey);
  const lsFlag = safeLSGet(lsKey);
  if (ssFlag) return { allow: true, reason: 'same_tab' };
  if (lsFlag) {
    return { allow: false, reason: 'reopened_after_close' };
  }
  // First ever visit — mark both.
  safeSSSet(ssKey, '1');
  safeLSSet(lsKey, String(Date.now()));
  return { allow: true, reason: 'first_load' };
};

const UpsellBanner = ({ sessionId, buyerEmail }) => {
  const navigate = useNavigate();
  const lifecycle = useMemo(() => resolveTabLifecycle(sessionId), [sessionId]);
  const [loading, setLoading] = useState(lifecycle.allow);
  // Normalize buyer email once (lower-cased, trimmed) for identity comparison
  // between the upsell origin order and the Cart page's logged-in user.
  const normalizedBuyerEmail = useMemo(() => {
    const e = (buyerEmail || '').trim().toLowerCase();
    return e || null;
  }, [buyerEmail]);
  const [offer, setOffer] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [modalConfig, setModalConfig] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const intervalRef = useRef(null);
  const closedReportedRef = useRef(false);
  const purchasingRef = useRef(false);

  // --- Close-after-reopen: immediately tell the backend, never render banner.
  useEffect(() => {
    if (!sessionId) return;
    if (lifecycle.allow) return;
    // Fire-and-forget: best effort, doesn't block anything.
    api.closeUpsellOffer(sessionId).catch(() => {});
  }, [sessionId, lifecycle.allow]);

  // --- Initial offer fetch (starts the timer server-side).
  useEffect(() => {
    if (!sessionId || !lifecycle.allow) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getUpsellOffer(sessionId);
        if (cancelled) return;
        const data = res?.data || {};
        if (!data.available || !data.offer || (data.remaining_seconds || 0) <= 0) {
          setLoading(false);
          return;
        }
        setOffer(data.offer);
        setRemaining(data.remaining_seconds);
        // The 'viewed' analytics event is recorded SERVER-SIDE on the first
        // call to `ensure_offer_started` (see upsell_service.py). We do NOT
        // emit it from the client, because every remount (refresh, re-nav)
        // would otherwise inflate the view count.
        // NOTE: we do NOT write `upsellWindow` to localStorage here. That
        // flag is what tells Cart to hide the coupon input, and we only
        // want that to happen if the buyer explicitly clicks an upsell
        // button. If they just close/leave the success page, the normal
        // coupon input must show on their next cart visit.
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Upsell offer fetch failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, lifecycle.allow]);

  // --- Client-side countdown (server remains authoritative).
  useEffect(() => {
    if (!offer || remaining <= 0 || dismissed) return undefined;
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [offer, dismissed, remaining > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the window expires locally, clean up the shared LS flag so Cart
  // re-enables the coupon input the next time it mounts.
  useEffect(() => {
    if (remaining === 0 && offer && !dismissed) {
      safeLSDel(LS_WINDOW_KEY);
      // Tell the backend the timer expired (so analytics reflect it even if
      // the user never interacted).
      api.trackUpsellAction(sessionId, 'expired', {}).catch(() => {});
    }
  }, [remaining, offer, dismissed, sessionId]);

  // --- pagehide beacon: if the user closes the tab mid-offer we mark closed.
  useEffect(() => {
    if (!sessionId || !lifecycle.allow) return undefined;
    const handler = () => {
      if (closedReportedRef.current) return;
      if (purchasingRef.current) return; // a redirect / purchase is in-flight
      closedReportedRef.current = true;
      try {
        const base = typeof api.getBaseUrl === 'function' ? api.getBaseUrl() : '';
        const url = `${base}/payments/upsell/${encodeURIComponent(sessionId)}/close/`;
        const body = new Blob([JSON.stringify({})], { type: 'application/json' });
        if (navigator.sendBeacon && base) {
          navigator.sendBeacon(url, body);
        } else {
          api.closeUpsellOffer(sessionId).catch(() => {});
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [sessionId, lifecycle.allow]);

  // --- Interactions ---------------------------------------------------------
  const handleDecline = useCallback(async () => {
    setDismissed(true);
    safeLSDel(LS_WINDOW_KEY);
    safeLSDel(LS_COUPON_KEY);
    try { await api.declineUpsellOffer(sessionId); } catch { /* best-effort */ }
  }, [sessionId]);

  const handleRouteToAmp = useCallback((column) => {
    purchasingRef.current = true; // don't fire 'closed' on unload after click
    const expiresAt = Date.now() + Math.max(1, remaining) * 1000;
    // The buyer CLICKED — now (and only now) do we tell Cart to:
    //   (a) hide the manual coupon input [via `upsellWindow`]
    //   (b) auto-apply UPSELL-AMP-20 on checkout [via `upsellAmpCoupon`]
    // Both entries are stamped with buyerEmail so a different logged-in
    // user on the same browser is never affected (cross-user leak fix).
    safeLSSet(LS_WINDOW_KEY, JSON.stringify({
      sessionId,
      caseKey: offer?.case_key || null,
      expiresAt,
      buyerEmail: normalizedBuyerEmail,
    }));
    // The backend mints a fresh, email-restricted, single-use coupon per
    // offer. If for some reason we didn't get one (old cached backend,
    // DB write failed, etc.) we skip writing the coupon entry entirely —
    // the buyer just pays full price on AMP tickets rather than getting
    // a silent, globally-valid discount applied to their cart.
    if (column.upsell_coupon) {
      safeLSSet(LS_COUPON_KEY, JSON.stringify({
        sessionId,
        code: column.upsell_coupon,
        targetRoute: column.target_route,
        expiresAt,
        buyerEmail: normalizedBuyerEmail,
      }));
    } else {
      safeLSDel(LS_COUPON_KEY);
    }
    api.trackUpsellAction(sessionId, 'clicked_seats', {
      target_route: column.target_route,
      case_key: offer?.case_key,
    }).catch(() => {});
    navigate(column.target_route || '/amphitheater-tickets');
  }, [navigate, sessionId, remaining, normalizedBuyerEmail, offer]);

  const handleOpenModal = useCallback((column) => {
    // Default to 1 as agreed — the stepper inside the modal lets the buyer
    // bump it up, but the starting value is intentionally minimal so we
    // don't surprise anyone with a 2x charge.
    setModalConfig({
      passSlug: column.pass_slug,
      passName: column.pass_name,
      priceCents: column.pass_price_cents,
      originalCents: column.pass_original_cents,
      // Fields forwarded to the modal so it can prefill /cart and auto-
      // apply the per-buyer upsell coupon (see FestivalPassModal props
      // for the full contract).
      passTicketTypeId: column.pass_ticket_type_id || null,
      passFullPriceCents: column.pass_full_price_cents || column.pass_original_cents || 0,
      passCouponCode: column.pass_coupon || null,
      quantity: 1,
    });
    api.trackUpsellAction(sessionId, 'opened_modal', {
      pass_slug: column.pass_slug,
      case_key: offer?.case_key,
    }).catch(() => {});
  }, [sessionId, offer]);

  const handleCloseModal = useCallback(() => {
    setModalConfig(null);
    api.trackUpsellAction(sessionId, 'closed_modal', {}).catch(() => {});
  }, [sessionId]);

  const handleModalPurchased = useCallback(() => {
    // New flow: the modal writes the pass into the cart + LS coupon and
    // navigates to /cart itself. We just need to stop firing the 'closed'
    // beacon on unload (the buyer is intentionally leaving the page).
    purchasingRef.current = true;
    setModalConfig(null);
    setDismissed(true);
    // Do NOT clear LS_WINDOW_KEY / LS_COUPON_KEY here — the modal just
    // wrote them and Cart needs to read them on the very next render.
  }, []);

  // --- Render gate ----------------------------------------------------------
  if (!lifecycle.allow) return null;
  if (loading || !offer || remaining <= 0 || dismissed) return null;

  const { heading, subtitle, left, right } = offer;

  const renderButton = (col) => {
    if (!col) return null;
    const onClick = col.action === 'modal_pass'
      ? () => handleOpenModal(col)
      : () => handleRouteToAmp(col);
    return (
      <div className="upsell-col">
        <p
          className="upsell-col__save"
          dangerouslySetInnerHTML={{ __html: col.save_line_html }}
        />
        <button type="button" className="upsell-col__button" onClick={onClick}>
          {col.button_text}
        </button>
        {col.caption && <div className="upsell-col__caption">{col.caption}</div>}
      </div>
    );
  };

  return (
    <>
      <div className="upsell-banner" role="region" aria-label="Post-purchase offer">
        <h2 className="upsell-banner__heading">
          {heading}
          <span className="upsell-flame" aria-hidden="true">🔥</span>
        </h2>
        <p className="upsell-banner__subtitle">{subtitle}</p>

        <div className="upsell-banner__timer" aria-live="polite">
          <span className="upsell-banner__timer-dot" aria-hidden="true" />
          Offer expires in {fmt(remaining)}
        </div>

        <div className="upsell-banner__columns">
          {renderButton(left)}
          {renderButton(right)}
        </div>

        <div className="upsell-banner__decline-row">
          <button type="button" className="upsell-banner__decline" onClick={handleDecline}>
            No thanks
          </button>
        </div>

        <div className="upsell-banner__footnotes">
          <p>*Cannot be combined with other offers or coupons.</p>
          <p>Pacific Amphitheater tickets include same-day festival admission.</p>
          <p>Festival Tickets do not include access to Pacific Amphitheater performances.</p>
          <p>Closing this page ends the offer permanently.</p>
        </div>
      </div>

      {modalConfig && (
        <FestivalPassModal
          sessionId={sessionId}
          passSlug={modalConfig.passSlug}
          passName={modalConfig.passName}
          priceCents={modalConfig.priceCents}
          originalCents={modalConfig.originalCents}
          defaultQuantity={modalConfig.quantity}
          passTicketTypeId={modalConfig.passTicketTypeId}
          passFullPriceCents={modalConfig.passFullPriceCents}
          passCouponCode={modalConfig.passCouponCode}
          buyerEmail={normalizedBuyerEmail}
          remainingSeconds={remaining}
          onClose={handleCloseModal}
          onPurchased={handleModalPurchased}
        />
      )}
    </>
  );
};

export default UpsellBanner;
